import { useState, useEffect, Fragment } from "react"
import { Box, Typography } from "@mui/material"
import Icon from "@mdi/react"
import { mdiAlert } from "@mdi/js"

import ConfigurationGroupDropDown from "./ConfigurationGroupDropDown"
import ConfigurationGroupSingleOrMulti from "./ConfigurationGroupSingleOrMulti"

import useTranslation from "hooks/useTranslation"

import fetchConfigurationGroupProducts from "@secureo/common/utils/commerceTools/graphql/queries/fetchConfigurationGroupProducts"

import {
	DispatchProductDetailPageReducer,
	setInitialConfigurations,
	ProductDetailPageReducerState,
	setSingleConfigurationGroupConfiguration,
	setMultiConfigurationGroupConfiguration,
	ProductConfigurationsBySku,
} from "@secureo/common/reducers/productDetailPageReducer"
import { ConfigurationGroupProductsBySku } from "@secureo/common/typings/Product"
import { CurrencyCode } from "typings/Forex"

const fetchConfigurationGroupProductsBySku = async (
	accessToken: string,
	configurationGroupProductSkus: string[],
	currencyCode: CurrencyCode,
	language: string,
) => {
	const configurationGroupProducts = await fetchConfigurationGroupProducts(
		accessToken,
		configurationGroupProductSkus,
		currencyCode,
		language,
	)

	const configurationGroupProductsBySku = configurationGroupProducts.reduce(
		(configurationGroupProductsBySku, configurationGroupProduct) => {
			const { sku } = configurationGroupProduct

			return {
				...configurationGroupProductsBySku,
				[sku]: configurationGroupProduct,
			}
		},
		{} as ConfigurationGroupProductsBySku,
	)

	return configurationGroupProductsBySku
}

const ConfigurationGroups = ({
	accessToken,
	reducerState,
	dispatch,
	isLockingSystemComponent = false,
	language,
}: Props) => {
	const { t } = useTranslation("product")
	const {
		isLoading,
		attributes: { configurationGroups = [] },
		configurationsByConfigurationGroup,
		taxRate,
		countryCode,
		currencyCode,
	} = reducerState

	const [configurationGroupProductsBySku, setConfigurationGroupProductsBySku] = useState(
		{} as ConfigurationGroupProductsBySku,
	)

	const filteredConfigurationGroups = isLockingSystemComponent
		? configurationGroups.filter(
				({ excludeFromLockingSystemComponent }) => !excludeFromLockingSystemComponent,
		  )
		: configurationGroups
	const configurationGroupProductSkus = filteredConfigurationGroups.reduce(
		(configurationGroupProductSkus, configurationGroup) => {
			const skus = configurationGroup.items.map(({ sku }) => sku)
			return [...configurationGroupProductSkus, ...skus]
		},
		[] as string[],
	)

	const onConfigurationGroupsFetched = (
		configurationGroupProductsBySku: ConfigurationGroupProductsBySku,
	) => {
		setInitialConfigurations(dispatch, reducerState, configurationGroupProductsBySku)
	}

	const onSingleConfigurationGroupConfigurationSet = (
		configurationGroupProductsBySku: ConfigurationGroupProductsBySku,
		configurationGroupName: string,
		sku: string,
	) => {
		setSingleConfigurationGroupConfiguration(
			dispatch,
			reducerState,
			configurationGroupProductsBySku,
			configurationGroupName,
			sku,
		)
	}

	const onMultiConfigurationGroupConfigurationSet = (
		configurationGroupProductsBySku: ConfigurationGroupProductsBySku,
		configurationGroupName: string,
		sku: string,
		quantity: number,
	) => {
		setMultiConfigurationGroupConfiguration(
			dispatch,
			reducerState,
			configurationGroupProductsBySku,
			configurationGroupName,
			sku,
			quantity,
		)
	}

	useEffect(() => {
		fetchConfigurationGroupProductsBySku(
			accessToken,
			configurationGroupProductSkus,
			currencyCode,
			language,
		).then((configurationGroupProductsBySku) => {
			setConfigurationGroupProductsBySku(configurationGroupProductsBySku)
			if (onConfigurationGroupsFetched) {
				onConfigurationGroupsFetched(configurationGroupProductsBySku)
			}
		})

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [configurationGroupProductSkus.toString(), currencyCode, language])

	return (
		<div>
			<Box mb={2}>
				<Typography
					component="div"
					style={{
						width: "100%",
						fontSize: "15px",
						display: "flex",
						alignItems: "center",
					}}
				>
					<Icon path={mdiAlert} size={0.8} color="#274041" />
					<Box ml={1}>
						<strong>{t("pleaseNote")}:</strong> {t("pleaseNoteText")}
					</Box>
				</Typography>
			</Box>

			{filteredConfigurationGroups.map((configurationGroup) => {
				const { name: configurationGroupName, type } = configurationGroup
				const selectedConfigurationsBySku: ProductConfigurationsBySku =
					configurationsByConfigurationGroup?.[configurationGroupName] ?? {}

				return (
					<Fragment key={configurationGroupName}>
						{type === "dropDown" ? (
							<ConfigurationGroupDropDown
								key={configurationGroupName}
								isLoading={isLoading}
								taxRate={taxRate}
								countryCode={countryCode}
								configurationGroup={configurationGroup}
								configurationGroupProductsBySku={configurationGroupProductsBySku}
								selectedConfigurationsBySku={selectedConfigurationsBySku}
								setSingleConfigurationGroupConfiguration={
									onSingleConfigurationGroupConfigurationSet
								}
							/>
						) : (
							<ConfigurationGroupSingleOrMulti
								isLoading={isLoading}
								taxRate={taxRate}
								countryCode={countryCode}
								configurationGroup={configurationGroup}
								configurationGroupProductsBySku={configurationGroupProductsBySku}
								selectedConfigurationsBySku={selectedConfigurationsBySku}
								onSingleConfigurationGroupConfigurationSet={
									onSingleConfigurationGroupConfigurationSet
								}
								onMultiConfigurationGroupConfigurationSet={
									onMultiConfigurationGroupConfigurationSet
								}
							/>
						)}
					</Fragment>
				)
			})}
		</div>
	)
}

interface Props {
	accessToken: string
	reducerState: ProductDetailPageReducerState
	dispatch: DispatchProductDetailPageReducer
	isLockingSystemComponent?: boolean
	language: string
}

export default ConfigurationGroups
